import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, Section, Segmented, Toggle } from './ui';

/**
 * A rolling display of a competition for the clubhouse screen: Home, Teams,
 * Draw, Scores, Results and Analytics in turn, each for a set number of
 * seconds. The screens themselves are the ordinary tab views, rendered by
 * App through `renderScreen` with admin controls off, so the show never
 * drifts from what the tabs look like.
 *
 * Draw and Scores can each be narrowed to chosen rounds. No rounds chosen
 * means all of them.
 */

const ROUND_SCREENS = ['fixtures', 'scores'];
const TEXT_SIZES = [
  { value: '1', label: 'Normal' },
  { value: '1.25', label: 'Large' },
  { value: '1.5', label: 'Extra large' },
];

const storageKey = compId => `greensheet_slideshow_${compId}`;

function loadSettings(compId, screens) {
  const defaults = {
    screens: screens.map(s => s.key),
    seconds: 20,
    rounds: { fixtures: [], scores: [] },
    zoom: '1',
    autoScroll: false,
  };
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(compId)) || 'null');
    if (!saved) return defaults;
    return {
      ...defaults,
      ...saved,
      screens: (saved.screens || defaults.screens).filter(k => screens.some(s => s.key === k)),
      rounds: { ...defaults.rounds, ...(saved.rounds || {}) },
    };
  } catch {
    return defaults;
  }
}

function roundsLabel(rounds) {
  if (!rounds || rounds.length === 0) return 'All rounds';
  return `${rounds.length === 1 ? 'Round' : 'Rounds'} ${rounds.join(', ')}`;
}

function CompetitionSlideshow({ matches, onClose, renderScreen, screens, selectedComp }) {
  const [settings, setSettings] = useState(() => loadSettings(selectedComp.id, screens));
  const [playing, setPlaying] = useState(false);

  const availableRounds = useMemo(
    () => [...new Set(matches.map(m => m.round).filter(r => r != null))].sort((a, b) => a - b),
    [matches]
  );

  const update = patch => setSettings(prev => ({ ...prev, ...patch }));

  const toggleScreen = key => update({
    screens: settings.screens.includes(key)
      ? settings.screens.filter(k => k !== key)
      // Keep the tab order rather than the order they were ticked in.
      : screens.map(s => s.key).filter(k => k === key || settings.screens.includes(k)),
  });

  const toggleRound = (screenKey, round) => {
    const current = settings.rounds[screenKey] || [];
    const next = round === 'all'
      ? []
      : current.includes(round) ? current.filter(r => r !== round) : [...current, round].sort((a, b) => a - b);
    update({ rounds: { ...settings.rounds, [screenKey]: next } });
  };

  const start = () => {
    try { localStorage.setItem(storageKey(selectedComp.id), JSON.stringify(settings)); } catch {}
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    setPlaying(true);
  };

  const stop = useCallback(() => {
    if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen().catch(() => {});
    onClose();
  }, [onClose]);

  if (playing) {
    return (
      <SlideshowPlayer
        matches={matches}
        onExit={stop}
        renderScreen={renderScreen}
        screens={screens.filter(s => settings.screens.includes(s.key))}
        selectedComp={selectedComp}
        settings={settings}
      />
    );
  }

  const seconds = Number(settings.seconds) || 0;
  const canStart = settings.screens.length > 0 && seconds >= 5;

  return (
    <div className="w3-modal" style={{ display: 'block', zIndex: 2000 }}>
      <div className="w3-modal-content w3-round gs-panel slideshow-setup">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          <h3 className="page-title" style={{ margin: 0 }}>Slideshow</h3>
          <button className="w3-button w3-round" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <Section label="Screens to show">
          <div className="gs-chip-group">
            {screens.map(s => {
              const on = settings.screens.includes(s.key);
              return (
                <label key={s.key} className={`gs-chip${on ? ' gs-chip-on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleScreen(s.key)} />
                  {s.label}
                </label>
              );
            })}
          </div>
        </Section>

        {ROUND_SCREENS.filter(k => settings.screens.includes(k)).map(screenKey => {
          const label = screens.find(s => s.key === screenKey).label;
          const chosen = settings.rounds[screenKey] || [];
          return (
            <Section key={screenKey} label={`${label} — rounds`}>
              {availableRounds.length === 0 ? (
                <p className="w3-text-grey" style={{ margin: 0 }}><em>No draw yet, so all rounds will show once there is one.</em></p>
              ) : (
                <div className="gs-chip-group" role="group" aria-label={`Rounds shown on ${label}`}>
                  <label className={`gs-chip${chosen.length === 0 ? ' gs-chip-on' : ''}`}>
                    <input type="checkbox" checked={chosen.length === 0} onChange={() => toggleRound(screenKey, 'all')} />
                    All
                  </label>
                  {availableRounds.map(r => {
                    const on = chosen.includes(r);
                    return (
                      <label key={r} className={`gs-chip${on ? ' gs-chip-on' : ''}`}>
                        <input type="checkbox" checked={on} onChange={() => toggleRound(screenKey, r)} />
                        Round {r}
                      </label>
                    );
                  })}
                </div>
              )}
            </Section>
          );
        })}

        <Section label="Timing and display">
          <div className="gs-field-grid" style={{ '--gs-cols': '1fr 2fr' }}>
            <Field label="Seconds per screen" htmlFor="slideshow-seconds"
              help={seconds < 5 ? 'At least 5 seconds' : `One full cycle takes about ${Math.round(seconds * settings.screens.length / 6) / 10} min`}>
              <input id="slideshow-seconds" type="number" min="5" step="5" className="w3-input w3-border w3-round"
                value={settings.seconds}
                onChange={e => update({ seconds: e.target.value })} />
            </Field>
            <Field label="Text size">
              <Segmented ariaLabel="Text size" options={TEXT_SIZES} value={settings.zoom}
                onChange={v => update({ zoom: v })} />
            </Field>
          </div>
          <Field help="A screen too tall for the display slowly scrolls to the bottom during its time">
            <Toggle label="Scroll long screens" checked={!!settings.autoScroll}
              onChange={v => update({ autoScroll: v })} />
          </Field>
        </Section>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: 'var(--gs-space-4, 16px)' }}>
          <button className="w3-button w3-light-grey w3-round" onClick={onClose}>Cancel</button>
          <button className="w3-button w3-theme w3-round" onClick={start} disabled={!canStart}>
            <i className="fa fa-play w3-margin-right"></i>Start slideshow
          </button>
        </div>
      </div>
    </div>
  );
}

function SlideshowPlayer({ matches, onExit, renderScreen, screens, selectedComp, settings }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const stageRef = useRef(null);
  const durationMs = Math.max(5, Number(settings.seconds) || 20) * 1000;

  // Fresh scores come from App's own 15-second refresh of the competition.
  // Nothing App hands down may sit in the timer's dependencies: each refresh
  // gives those new identities, which restarted the timer before it fired.
  const go = useCallback(step => {
    setIndex(i => (i + step + screens.length) % screens.length);
  }, [screens.length]);

  useEffect(() => {
    if (paused || screens.length < 2) return undefined;
    const t = setTimeout(() => go(1), durationMs);
    return () => clearTimeout(t);
  }, [index, paused, durationMs, go, screens.length]);

  // Each screen starts at the top. With auto-scroll on, a screen taller than
  // the display then drifts down to its end, pausing at the top and bottom,
  // so nothing below the fold is skipped.
  const autoScroll = !!settings.autoScroll;
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    el.scrollTop = 0;
    if (!autoScroll || paused) return undefined;
    const hold = Math.min(3000, durationMs * 0.2);
    const travel = durationMs - hold * 2;
    let frame;
    let startedAt;
    const tick = now => {
      if (startedAt === undefined) startedAt = now;
      const t = Math.min(1, Math.max(0, (now - startedAt - hold) / travel));
      el.scrollTop = (el.scrollHeight - el.clientHeight) * t;
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [index, paused, durationMs, autoScroll]);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onExit();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === ' ') { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onExit]);

  const screen = screens[index];
  const isRoundScreen = ROUND_SCREENS.includes(screen.key);
  const rounds = isRoundScreen ? (settings.rounds[screen.key] || []) : [];
  const shownMatches = rounds.length ? matches.filter(m => rounds.includes(m.round)) : matches;

  return (
    <div className="slideshow" role="region" aria-label="Competition slideshow">
      <div className="slideshow-bar">
        <div className="slideshow-title">
          <strong>{selectedComp.name}</strong>
          <span>{screen.label}{isRoundScreen ? ` · ${roundsLabel(rounds)}` : ''}</span>
        </div>
        <div className="slideshow-dots" aria-hidden="true">
          {screens.map((s, i) => <span key={s.key} className={i === index ? 'on' : ''} />)}
        </div>
        <div className="slideshow-controls">
          <button onClick={() => go(-1)} aria-label="Previous screen"><i className="fa fa-chevron-left"></i></button>
          <button onClick={() => setPaused(p => !p)} aria-label={paused ? 'Play' : 'Pause'}>
            <i className={`fa ${paused ? 'fa-play' : 'fa-pause'}`}></i>
          </button>
          <button onClick={() => go(1)} aria-label="Next screen"><i className="fa fa-chevron-right"></i></button>
          <button onClick={onExit} aria-label="Exit slideshow"><i className="fa fa-times"></i></button>
        </div>
      </div>
      {!paused && screens.length > 1 && (
        <div className="slideshow-progress" key={index} style={{ animationDuration: `${durationMs}ms` }} />
      )}
      <div className="slideshow-stage" ref={stageRef}>
        <div className="slideshow-content" style={{ zoom: settings.zoom }}>
          {renderScreen(screen.key, shownMatches)}
        </div>
      </div>
    </div>
  );
}

export default CompetitionSlideshow;
